import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, OutLink, StatGrid } from "@/components/site/PageBits";
import { SectionLabel } from "@/components/site/FormBits";
import { useSeo, pageSeo } from "@/lib/seo";

const SEO = pageSeo("/nicaragua");

const SRC = "https://gregmiceli.com/nicaragua";

const BUILT = [
  ["All-in-One Systems", "Not just websites — complete business platforms, each launched with a live client and ready to be re-skinned for others in the same industry."],
  ["Nicaraguan websites", "Each site designed, launched and tuned by hand for a business operating in Nicaragua — hotels, restaurants, surf camps and tour operators."],
  ["Nica Bros", "A browser-based arcade game inspired by Nicaragua's people, cities, landscapes, wildlife, food, music, and culture."],
];

export default function NicaraguaPage() {
  useSeo(SEO);
  return (
    <article>
      <PageHero eyebrow="NICARAGUA · BUILT IN NICARAGUA, FOR NICARAGUA" title="Nicaragua" image="/brand/esc-aerial.jpg" imageAlt="Aerial view of the Pacific coast near Popoyo, Nicaragua"
        subtitle="From San Juan del Sur to Popoyo, Managua and Granada — apps, websites and complete All-in-One Systems launched for Nicaraguan businesses, hotels, restaurants, surf camps and tour operators.">
        <OutLink href={SRC} testId="nicaragua-external-link" light>Visit gregmiceli.com/nicaragua</OutLink>
      </PageHero>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <SectionLabel>Remote work in Nicaragua, by the numbers</SectionLabel>
        <h2 className="mt-3 mb-8 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">Real projects, built while living on the Pacific coast</h2>
        <StatGrid stats={[["25+", "Nicaragua sites launched"], ["56", "Websites built in 2026"], ["7", "Towns served"], ["2", "Companies created & sold in Nicaragua"]]} />
        <p className="mt-4 text-xs text-slate-500">Figures as reported on <a href={SRC} target="_blank" rel="noopener" className="underline">gregmiceli.com/nicaragua</a>. Shared as experience, not as a promise of anyone else's results.</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-8 grid md:grid-cols-3 gap-5">
        {BUILT.map(([t, d]) => (
          <div key={t} className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6">
            <h3 className="font-semibold text-lg text-[#0B2A4A]">{t}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <img src="/brand/nica-taxi.jpg" alt="Nicaragua Taxi Services — taxi and transport across Nicaragua" className="rounded-2xl w-full max-h-[420px] object-cover" data-testid="nicaragua-taxi-image" />
        <div>
          <SectionLabel>Companies created & sold in Nicaragua</SectionLabel>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">Destination Nica & Nicaragua Taxi Services</h2>
          <p className="mt-4 text-slate-700 leading-relaxed">Destination Nica grew into a resource for travelers, expatriates, investors and families interested in Nicaragua, and Nicaragua Taxi Services connected travelers with reliable local drivers. Both were built and successfully sold — examples of what testing a real local need can look like.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <div className="rounded-3xl bg-[#F5ECD7] p-8 sm:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl font-bold text-[#0B2A4A]">Want to work remotely from Nicaragua?</h2>
            <p className="mt-2 text-slate-700 max-w-xl">Start with a realistic, evidence-based test. Treat Nicaragua as a real home with residents, not just a backdrop — and verify entry, work, and tax questions with official sources.</p>
          </div>
          <Link to="/digital-nomad-beginners-course" className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-6 py-3.5 font-semibold hover:bg-[#D96F28]" data-testid="nicaragua-course-cta">See the beginners course <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </article>
  );
}
